/**
 * Schlanker API-Client: Session-Cookie, CSRF-Header und – falls die Seite
 * ohne eigene Domain aufgerufen wird – die Mandantenkennung.
 */

const TENANT_KEY = 'sp-tenant';
let csrfToken: string | null = null;

export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

/** Übernimmt ?tenant=<slug> aus der Adresse (Links aus E-Mails, Entwicklung). */
export function initTenantFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const slug = params.get('tenant');
  if (slug && /^[a-z0-9-]{1,48}$/.test(slug)) {
    try {
      localStorage.setItem(TENANT_KEY, slug);
    } catch {
      /* Speicher nicht verfügbar */
    }
  }
}

export function getTenantSlug(): string | null {
  try {
    return localStorage.getItem(TENANT_KEY);
  } catch {
    return null;
  }
}

export function setTenantSlug(slug: string | null) {
  try {
    if (slug) localStorage.setItem(TENANT_KEY, slug);
    else localStorage.removeItem(TENANT_KEY);
  } catch {
    /* Speicher nicht verfügbar */
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: { path: string; message: string }[],
  ) {
    super(message);
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/*
 * Bewusst aktivierte Bearbeitung gesperrter Vorgänge (nur Administratoren): Änderungen an Pfaden
 * mit diesem Präfix tragen die Begründung als Kopfzeile x-unlock-reason; der Server protokolliert sie.
 */
const unlockScopes = new Map<string, { reason: string; expires: number }>();
export function setUnlock(prefix: string, reason: string | null) {
  if (reason) unlockScopes.set(prefix, { reason, expires: Date.now() + 30 * 60_000 });
  else unlockScopes.delete(prefix);
}
function unlockFor(path: string) {
  for (const [prefix, u] of unlockScopes) {
    if (u.expires < Date.now()) unlockScopes.delete(prefix);
    else if (path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`)) return u.reason;
  }
  return null;
}

export async function api<T = unknown>(path: string, options: { method?: Method; body?: unknown; form?: FormData; headers?: Record<string, string> } = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json', ...options.headers };
  const slug = getTenantSlug();
  if (slug) headers['x-tenant-slug'] = slug;
  if (method !== 'GET' && csrfToken) headers['x-csrf-token'] = csrfToken;
  const unlock = method !== 'GET' ? unlockFor(path) : null;
  if (unlock) headers['x-unlock-reason'] = encodeURIComponent(unlock);

  let body: BodyInit | undefined;
  if (options.form) body = options.form;
  else if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  const res = await fetch(`/api${path}`, { method, headers, body, credentials: 'same-origin' });
  if (res.status === 204) return undefined as T;
  const data = res.headers.get('content-type')?.includes('application/json') ? await res.json() : null;
  if (!res.ok) {
    throw new ApiError(res.status, data?.error ?? 'ERROR', data?.message ?? `Fehler ${res.status}`, data?.details);
  }
  return data as T;
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.details?.length) return `${error.message} ${error.details.map((d) => `${d.path}: ${d.message}`).join('; ')}`;
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Unbekannter Fehler';
}
